import { Check as CheckIcon, Close as CloseIcon, Assessment as AssessmentIcon } from "@mui/icons-material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { UpdateApplicationStatusThunk } from "../../thunks";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import Moment from "react-moment";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useApiFeedback } from "../../../../app/useApiFeedback";
import CustomModal from "../../../../common/custom/CustomModal";
import MessageResponse from "../../../../common/feedback/messageResponse";
import CircularLoader from "../../../../common/loaders/circularLoader";
import { APPLICATION_STAGE, normalizeApplicationStage } from "../../../../common/models/enums/applicationStage";
import CustomTable from "../../../../common/tables/table";
import { fetchApplicantByIdThunk } from "../../../applicant/ApplicantSlice";
import { AllApplicantDto } from "../../../applicant/Models";
import { selectAdmin } from "../../adminSlice";
import {
  fetchNotificationsThunk,
  GetDocumentsForApplicantThunk,
} from "../../thunks";
import ViewAppTabs from "../ViewAppTabs";
import ViewApplication from "./../Applications/ViewApplication";
import EditApplication from "./../EditApplication";
import FilterApplications from "./../FilterApplications";
import { BackendApplicationStage } from "../../../../common/dropdowns/ApplicationStatus";
import { getApplicationStageName } from "../../../../common/Logic/commonLogic";

const AllApplicants = () => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<APPLICATION_STAGE>(
    APPLICATION_STAGE.All
  );
  const [search, setSearch] = useState("");
  const [isSearchEmpty, setIsSearchEmpty] = useState(true);
  const dispatch = useAppDispatch();
  const applicants = useAppSelector(selectAdmin);
  const [data, setData] = useState<AllApplicantDto[]>([]);
  const [filteredData, setFilteredData] = useState<AllApplicantDto[]>([]);
  const { feedback, handleApiResponse } = useApiFeedback();
  const [activeModal, setActiveModal] = useState<{
    applicationId: number | null;
    tabValue: number;
  }>({ applicationId: null, tabValue: 0 });

  const columns = [
    "#",
    "Name",
    "Surname",
    "ID Number",
    "Gender",
    "Race",
    "Application Status",
    "Status",
    "Date Completed",
    "Assessment Status",
    "Actions",
  ];

  // Memoized filtered data for pagination
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  useEffect(() => {
    const list = applicants.applicantState.applicants;
    if (list !== undefined) {
      setData(list);
      setFilteredData(list);
    }
  }, [applicants.applicantState.applicants]);

  // Format application stage for display
  const formatApplicationStage = (stage: string, isAssessed: boolean = false) => {
    // If the application is assessed, we don't show the 'Assessed' stage
    if (isAssessed && stage.includes('Assessed') && !stage.includes('ApplicationFullyAssessedAndIsPendingUniversitySelection')) {
      return 'In Progress';
    }
    
    // Handle specific stage formatting
    if (stage.includes('ApplicationFullyAssessedAndIsPendingUniversitySelection')) {
      return 'Application Fully Assessed And Is Pending University Selection';
    }
    if (stage.includes('ApplicationInReview')) {
      return 'Application In Review';
    }
    if (stage.includes('ApplicationDeclined')) {
      return 'Application Declined';
    }
    if (stage.includes('ApplicationSuccessful')) {
      return 'Application Successful';
    }
    // Default formatting for other stages
    return stage
      .replace(/([A-Z])/g, ' $1')
      .replace(/^\s+/, '')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Render status chip
  const renderStatusChip = (dateCompleted: Date) => {
    const isCompleted = dateCompleted !== null;
    return (
      <Chip
        label={isCompleted ? "Completed" : "In progress"}
        sx={{ color: isCompleted ? "green" : "orange" }}
        size="small"
      />
    );
  };

  // Handle assessment update
  const handleAssessApplication = async (applicationId: number) => {
    const payload = {
      applicationId: applicationId,
      applicationStage: APPLICATION_STAGE.Assessed,
      applicationStatus: APPLICATION_STAGE.Assessed,
      universityChoiceId: -1, // Not used for assessment
      declineReasonId: [], // Not used for assessment
      comment: "Application assessed by admin",
      sendEmail: false
    };
    
    await dispatch(UpdateApplicationStatusThunk(payload));
  };

  // Render assessment status
  const renderAssessmentStatus = (record: AllApplicantDto) => {
    if (record.isAssessed) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {record.guardianAssessementAudit?.isLegible ? (
            <Tooltip
              title={`${record.personalDetails.name} meets Means Test Criteria`}
            >
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccountBalanceWalletIcon
                      fontSize="small"
                      sx={{ color: "green", mr: 0.5 }}
                    />
                    {record.assessmentStatus}
                  </Box>
                }
                sx={{ 
                  border: "2px solid green", 
                  backgroundColor: "#fff",
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
                size="small"
              />
            </Tooltip>
          ) : (
            <Tooltip
              title={`${record.personalDetails.name} does not meet Means Test Criteria`}
            >
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccountBalanceWalletIcon
                      fontSize="small"
                      sx={{ color: "red", mr: 0.5 }}
                    />
                    {record.assessmentStatus}
                  </Box>
                }
                sx={{ 
                  border: "2px solid red", 
                  backgroundColor: "#fff",
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
                size="small"
              />
            </Tooltip>
          )}
        </Box>
      );
    }

    return (
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon fontSize="small" sx={{ mr: 0.5 }} />
            Not Assessed
          </Box>
        }
        onClick={() => handleAssessApplication(record.applicationId)}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
        size="small"
      />
    );
  };

  // Handle opening modal for a specific application
  const handleOpenModal = (record: AllApplicantDto) => {
    setActiveModal({
      applicationId: record.applicationId,
      tabValue: 0,
    });

    dispatch(fetchApplicantByIdThunk(record.applicationId)).then(
      (data: any) => {
        handleApiResponse(data, false);
      }
    );
    dispatch(GetDocumentsForApplicantThunk(record.applicationId));
    dispatch(fetchNotificationsThunk());
  };

  const mapStage = (stage: APPLICATION_STAGE | string) => {
    const norm = normalizeApplicationStage(stage);
    const stageStr =
      norm !== null ? String(APPLICATION_STAGE[norm]) : String(stage);

    if (stageStr.includes('ApplicationDeclined')) {
      return 'declinedApplication';
    }
    if (stageStr.includes('ApplicationSuccessful')) {
      return 'successfulApplication';
    }
    if (stageStr.includes('ApplicationInReview')) {
      return 'inReviewApplication';
    }
    if (stageStr.includes('ApplicationFullyAssessedAndIsPendingUniversitySelection')) {
      return 'pendingUniversitySelection';
    }
    return '';
  };

  const mapStageIcon = (stage: APPLICATION_STAGE | string) => {
    const norm = normalizeApplicationStage(stage);
    const stageStr =
      norm !== null ? String(APPLICATION_STAGE[norm]) : String(stage);

    if (stageStr.includes('ApplicationDeclined')) {
      return (
        <CloseIcon fontSize="small" sx={{ color: "red", mb: -0.49, mr: 1 }} />
      );
    }
    if (stageStr.includes('ApplicationSuccessful')) {
      return (
        <CheckIcon fontSize="small" sx={{ color: "green", mb: -0.49, mr: 1 }} />
      );
    }
    if (stageStr.includes('ApplicationInReview')) {
      return (
        <AssessmentIcon fontSize="small" sx={{ color: "orange", mb: -0.49, mr: 1 }} />
      );
    }
    if (stageStr.includes('ApplicationFullyAssessedAndIsPendingUniversitySelection')) {
      return (
        <AccountBalanceWalletIcon fontSize="small" sx={{ color: "blue", mb: -0.49, mr: 1 }} />
      );
    }
    return "";
  };

  // Create table rows
  const createTableRows = () => {
    return paginatedData.map((record, index) => (
      <TableRow key={index}>
        <TableCell
          className={`
          ${mapStage(record.applicationStage)}
        `}
        >
          {index + 1}
        </TableCell>
        <TableCell>{record.personalDetails.name}</TableCell>
        <TableCell>{record.personalDetails.surname}</TableCell>
        <TableCell>{record.personalDetails.identityNumber}</TableCell>
        <TableCell>{record.personalDetails.gender}</TableCell>
        <TableCell>{record.personalDetails.race}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {mapStageIcon(record.applicationStage)}
              {formatApplicationStage(record.applicationStage.toString(), record.isAssessed)}
            </Box>
          </Box>
        </TableCell>
        <TableCell>{renderStatusChip(record.dateCompleted)}</TableCell>
        <TableCell>
          {record.dateCompleted ? (
            <Moment format="YYYY/MM/DD" date={record.dateCompleted} />
          ) : (
            <Box sx={{ color: "orange" }}>Application Inprogress</Box>
          )}
        </TableCell>
        <TableCell sx={{ width: 200 }}>
          {renderAssessmentStatus(record)}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={2}>
            <Tooltip title="View Application">
              <IconButton onClick={() => handleOpenModal(record)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <CustomModal
              maxWidth="lg"
              open={activeModal.applicationId === record.applicationId}
              onClose={() =>
                setActiveModal({ ...activeModal, applicationId: null })
              }
              title="View Application"
              dialogTitle={
                <ViewAppTabs
                  setValue={(newValue) =>
                    setActiveModal({
                      ...activeModal,
                      tabValue: newValue as number,
                    })
                  }
                  value={activeModal.tabValue}
                />
              }
            >
              <ViewApplication
                applicationId={record.applicationId}
                currentApplicationStatus={
                  (normalizeApplicationStage(record.applicationStage) ??
                    APPLICATION_STAGE.PersonalDetails) as BackendApplicationStage
                }
                setFilterd={setFilter}
                applicantName={record.personalDetails.name}
                tabValue={activeModal.tabValue}
                application={record}
              />
            </CustomModal>
            <EditApplication application={record} />
          </Stack>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Applications
      </Typography>

      <MessageResponse feedback={feedback} />
      <FilterApplications
        filter={filter}
        applicants={data}
        setFilterdData={setFilteredData}
        setFilterd={setFilter}
        setSearch={setSearch}
        search={search}
        setIsSearchEmpty={setIsSearchEmpty}
        setPage={setPage}
      />

      <CircularLoader status={applicants.applicantState.status} />

      {applicants.applicantState.status === "idle" && (
        <CustomTable
          columns={columns}
          data={filteredData}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          rows={createTableRows}
          page={page}
        />
      )}
    </Box>
  );
};

export default AllApplicants;
